import React, {  useCallback, useEffect, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';

import moment from 'moment';
import styled from 'styled-components';
import classNames from 'classnames';
import _ from 'lodash';

import { getAllCertificates, getDashboardOverview } from '../../../config/redux/actions/admin';
import { setCertificateStatus, setCurrentPage, setField, setPageSize, setQuery, setSortColumn } from '../../../config/redux/slice/adminSlice';
import { paginate } from '../../../utils/lib';
import { CERTIFICATE_STATUS } from '../../../constants';

import AppWrapper from '../../layout/AppWrapper';
import DashboardCard from '../../components/DashboardCard';
import Pagination from '../../components/Pagination';
import Skeleton from '../../components/Skeleton';
import Select from '../../components/Select';

const Container = styled.div`
    background-color: #ffffff;
    padding: 1.5rem;
    border-radius: 1rem;
`;

const Title = styled.div`
    color: #05004E;
    font-size: 1.25rem;
    font-weight: 700;
`;

const Subtitle = styled.div`
    color: #737791;
    font-size: 1rem;
    font-weight: 400;
    margin-top: 0.25rem;
    margin-bottom: 2.68rem;
`;

const Table = styled.table`
    border-collapse: collapse;
    font-size: 0.9em;
    width: 100%;
`;

const Input = styled.input`
    height: 3rem;
    width: 100%;
    min-width: 18rem;
    padding: 0 0.5rem;
    border: 1px solid #eeeeee;
    background-color: #ffffff;
    border-radius: 0.25rem;
`;

const AllCertificates = () => {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin);

    useEffect(() => {
        dispatch(getAllCertificates(admin.certificates.status));
    }, [dispatch, admin.certificates.status]);

    useEffect(() => {
        dispatch(setField({ field: 'CERTIFICATE_NUMBER' }));
        dispatch(getDashboardOverview());
    }, [dispatch]);
 
    const paginatedCertificates = useMemo(() => {
        let filtered = admin.certificates.list;

        if (admin.filterQuery.query) {
            const query = admin.filterQuery.query.toLowerCase();

            filtered = filtered.filter((certificate) => {
                const value = _.get(certificate, admin.filterQuery.field);
                if (value) {
                    return value.toLowerCase().indexOf(query) !== -1;
                }

                return false;
            });
        }

        if (admin.sortColumn) {
            filtered = _.orderBy(filtered, [admin.sortColumn.path], admin.sortColumn.order);
        }

        return paginate(filtered, admin.filterQuery.currentPage, admin.filterQuery.pageSize);
    }, [admin.filterQuery, admin.certificates, admin.sortColumn]);

    const DATA = useMemo(() => {
        return [
            {
                backgroundColor: '#FFE2E5' ,
                iconBackground: '#FA5A7D',
                value: admin.overview.total_certificates,
                icon: "certificate",
                label: 'Total certificates' ,
                subLabel: 'Total uploaded in the system' ,
            },
            {
                backgroundColor: '#FFF4DE' ,
                iconBackground: '#FF947A',
                value: admin.overview.pending_certificates,
                icon: "clock",
                label: 'Pending approval' ,
                subLabel: 'Still awaiting approval' ,
            },
            {
                backgroundColor: '#DCFCE7' ,
                iconBackground: '#3CD856',
                value: admin.overview.approved_certificates,
                icon: "check-decagram",
                label: 'Approved certificates' ,
                subLabel: 'Already validated and approved' ,
            },
            {
                backgroundColor: '#ffc1d1' ,
                iconBackground: '#B00020',
                value: admin.overview.rejected_certificates,
                icon: "message-alert",
                label: 'Rejected certificates' ,
                subLabel: 'Rejected for disbursal' ,
            },
            {
                backgroundColor: '#F3E8FF' ,
                iconBackground: '#BF83FF',
                value: admin.overview.disbursed_certificates,
                icon: "checkbox-multiple-marked",
                label: 'Disbursed' ,
                subLabel: 'Total disbursed' ,
            },
        ];
    }, [admin.overview]);

    const handlePageSizeChange = useCallback((event) => {
        dispatch(setPageSize({ pageSize: parseInt(event.target.value) }));
        dispatch(setCurrentPage({ currentPage: 1 }));
    }, [dispatch]);

    return ( 
        <AppWrapper>
            <Container>
                <Title>Overview</Title>
                <Subtitle>Certificate Summary</Subtitle>
                
                <div className="container-fluid">
                    <div className="row gx-5">
                        {DATA.map((item, index) => (
                            <div className={classNames({ "col p-0": true, "mr-3": index !== (DATA.length - 1)})} key={item.label}>
                                <DashboardCard
                                    backgroundColor={item.backgroundColor} 
                                    iconBackground={item.iconBackground}
                                    value={item.value} 
                                    icon={item.icon}
                                    label={item.label}
                                    subLabel={item.subLabel} 
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </Container>

            <Container className='mt-5 p-0 overflow-hidden'>
                <div className="d-flex justify-content-between align-items-center p-5">
                    <Title>Certificates</Title>

                    <div className='d-flex justify-content-center align-items-center'>
                        <Select 
                            placeholder="Filter by certificate status"
                            options={FILTER_OPTIONS} 
                            value={admin.certificates.status} 
                            onChange={(event) => dispatch(setCertificateStatus(event.target.value))} 
                        />
                        
                        <div className="mx-2">
                            <Select 
                                placeholder="Select search criteria"
                                options={FILTER_FIELDS} 
                                value={admin.filterQuery.field} 
                                onChange={(event) => dispatch(setField({ field: event.target.value }))} 
                            />
                        </div>
                        
                        <Input 
                            type='text'
                            placeholder='Enter Value'
                            value={admin.filterQuery.query} 
                            onChange={(event) => dispatch(setQuery({ query: event.target.value }))} 
                        />

                        <CSVLink className='w-100 ml-2' data={paginatedCertificates}>
                            Export as CSV
                        </CSVLink>
                    </div>
                </div>

                <Table>
                    <thead className='p-3'>
                        <tr className='bg-light text-left text-muted'>
                            {TABLE_HEADER.map((header) => (
                                <th 
                                    className='p-4' 
                                    key={header.id} 
                                    onClick={() => dispatch(setSortColumn({ path: header.id, order: admin.sortColumn.order === 'asc' ? 'desc' : 'asc' }))}
                                >
                                    {header.name} {admin.sortColumn.path === header.id && <i className={classNames({ 
                                        "mdi": true, 
                                        "mdi-arrow-up": admin.sortColumn.order === 'asc',
                                        "mdi-arrow-down": admin.sortColumn.order === 'desc'
                                        })}></i>} 
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {admin.isLoading 
                            ? _.range(1, 6).map((filler) => (
                                <tr key={filler} className='border-bottom text-left text-dark'>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                </tr>
                            ))
                            : paginatedCertificates.map((certificate) => (
                                <tr key={certificate.ID} className='border-bottom text-left text-dark'>
                                    <td className='p-4'>{certificate.ID}</td>
                                    <td className='p-4'>{certificate.CERTIFICATE_NUMBER}</td>
                                    <td className='p-4'>{certificate.CERTIFICATE_NUMBER_TWO}</td>
                                    <td className='p-4'>{moment(certificate.EFFECTIVE_DATE).format('DD MMM YYYY')}</td>
                                    <td className='p-4'>{moment(certificate.EXPIRY_DATE).format('DD MMM YYYY')}</td>
                                    <td className='p-4'>{certificate.CURRENCY}</td>
                                    <td className='p-4'>
                                        <NumericFormat
                                            allowLeadingZeros={false}
                                            displayType='text'
                                            thousandSeparator=','
                                            decimalSeparator='.'
                                            value={certificate.AMOUNT_ON_CERTIFICATE}
                                        />
                                    </td>
                                    <td className='p-4'>
                                        <NumericFormat
                                            allowLeadingZeros={false}
                                            displayType='text'
                                            thousandSeparator=','
                                            decimalSeparator='.'
                                            value={certificate.AMOUNT_REQUEST}
                                        />
                                    </td>
                                    <td className='p-4'>
                                        <span className={classNames({
                                            "badge p-2": true,
                                            "badge-danger": certificate.CERTIFICAATE_STATUS === CERTIFICATE_STATUS.REJECTED,
                                            "badge-success": certificate.CERTIFICATE_STATUS === CERTIFICATE_STATUS.APPROVED,
                                            "badge-warning": certificate.CERTIFICATE_STATUS === CERTIFICATE_STATUS.PENDING,
                                            "badge-info": certificate.CERTIFICATE_STATUS === CERTIFICATE_STATUS.DISBURSED,
                                            "badge-primary": certificate.CERTIFICATE_STATUS === CERTIFICATE_STATUS.CONFIRMED,
                                            "badge-dark": certificate.CERTIFICAATE_STATUS === CERTIFICATE_STATUS.CLOSED,
                                        })}>{certificate.CERTIFICATE_STATUS}</span>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center p-5">
                    <div>
                        <Select 
                            placeholder="Select Items Per Page"
                            options={PAGE_SIZE_OPTIONS} 
                            value={admin.filterQuery.pageSize} 
                            onChange={handlePageSizeChange} 
                        />
                    </div>
                   
                    <div>
                        <Pagination 
                            currentPage={admin.filterQuery.currentPage}
                            itemsCount={admin.certificates.list.length}
                            pageSize={admin.filterQuery.pageSize}
                            onPageChange={(page) => dispatch(setCurrentPage({ currentPage: page }))}
                        />
                    </div>
                </div>
            </Container>
        </AppWrapper>
    );
};

const FILTER_OPTIONS = [
    {
        id: 'all',
        name: 'All'
    },
    {
        id: 'pending',
        name: 'Pending'
    },
    {
        id: 'confirmed',
        name: 'Confirmed'
    },
    {
        id: 'approved',
        name: 'Approved'
    },
    {
        id: 'disbursed',
        name: 'Disbursed'
    }, 
];

const FILTER_FIELDS = [
    {
        id: 'CERTIFICATE_NUMBER',
        name: 'CERTIFICATE NUMBER'
    },
    {
        id: 'CERTIFICATE_NUMBER_TWO',
        name: 'CERTIFICATE CODE'
    },
    {
        id: 'CURRENCY',
        name: 'CURRENCY'
    },
    {
        id: 'AMOUNT_ON_CERTIFICATE',
        name: 'AMOUNT ON CERTIFICATE'
    },
    {
        id: 'AMOUNT_REQUEST',
        name: 'AMOUNT REQUESTED'
    },  
    {
        id: 'CERTIFICATE_STATUS',
        name: 'CERTIFICATE STATUS'
    },  
];

const TABLE_HEADER = [
    {
        id: 'ID',
        name: '#'
    },
    {
        id: 'CERTIFICATE_NUMBER',
        name: 'CERTIFICATE NUMBER'
    },
    {
        id: 'CERTIFICATE_NUMBER_TWO',
        name: 'CERTIFICATE CODE'
    },
    {
        id: 'EFFECTIVE DATE',
        name: 'CERTIFICATE ISSUED ON'
    },
    {
        id: 'EXPIRY_DATE',
        name: 'CERTIFICATE EXPIRES ON'
    },  
    {
        id: 'CURRENCY',
        name: 'CURRENCY'
    },
    {
        id: 'AMOUNT_ON_CERTIFICATE',
        name: 'AMOUNT ON CERTIFICATE'
    },
    {
        id: 'AMOUNT_REQUEST',
        name: 'AMOUNT REQUESTED'
    },  
    {
        id: 'CERTIFICATE_STATUS',
        name: 'CERTIFICATE STATUS'
    },  
];

const PAGE_SIZE_OPTIONS = _.range(1, 21).map((value) => ({ id: value, name: value }));
    
export default AllCertificates;